import React, { useEffect, useLayoutEffect } from 'react';

// Redux connect
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Link
import { Link, useLocation, useNavigate } from 'react-router-dom';

// components
import Button from 'components/UiKit/Button';
import TextField from 'components/UiKit/FieldText';
import ErrorBoundary from 'components/ErrorBoundary';

// Lib
import * as yup from 'yup';
import Cookies from 'js-cookie';
import { sanitize } from 'dompurify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

// Actions
import * as actionsCreator from '../reducer';

// Constants
import { amplitudeInstance, events } from 'constans/cookie';

// Redux
import { shallowEqual } from 'react-redux';

// Schema
const schema = yup.object({
	password: yup.string().required(),
	email: yup.string().email().required()
}).required();
type FormData = yup.InferType<typeof schema>;

export const SignIn = () => {
	const { state: isBiosearch = '' } = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const {
		status,
		message,
		loading
	} = useAppSelector((store) => {
		return {
			status: store.auth.status,
			message: store.auth.message,
			loading: store.auth.loadingSingIn
		};
	}, shallowEqual);

	useEffect(() => () => {
		dispatch(actionsCreator.clearMessage());
	}, []);

	// Scroll to top after navigating
	useLayoutEffect(() => {
		window.scrollTo(0, 0);
	}, [navigate]);

	useEffect(() => {
		document.body.classList.add('wrapper-singin');

		return () => document.body.classList.remove('wrapper-singin');
	}, []);

	const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
		resolver: yupResolver(schema)
	});

	const onSubmit = (data: FormData) => {
		const isCookie = Cookies.get('cookieName');
		if (!!isCookie) {
			amplitudeInstance.logEvent(events.login_click);
		}

		dispatch(actionsCreator.signIn({ ...data, navigate, isBiosearch }));
	};

	const errorMessage = message === 'Your account is not verified, please check email to verify email.' ?
		`Your account is not verified, please check email to verify. <a href="/resend-mail">Resend verifycation email</a>`
		: message || '';

	return (
		<ErrorBoundary>
			<div className="screen-signin">
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="screen-signin__form">
						<h2 className="screen-signin__title">
							<i>If you are already registered with <b>Enamine Store</b>, please log in here:</i>
						</h2>

						<div className="flex flex-ai-center flex-direction-column-desktop">
							<TextField
								required
								name="email"
								label="Email address*"
								className="screen-signin__col mr30"
								placeholder="yourcompany@email.com"
								msgError={errors?.email ? errors?.email.message : ''}
								register={register('email', { required: 'Email is required', pattern: /^\S+@\S+$/i })}
							/>

							<TextField
								required
								isShowPassword
								name="password"
								label="Password *"
								placeholder="Your password"
								isError={!!errors?.password}
								className="screen-signin__col"
								msgError={errors?.password ? errors?.password.message : ''}
								register={register('password', { required: 'Password is required' })}
							/>
						</div>
						<div className="mt10 text-al-right">
							<Link className="link" to="/forgot-password">Forgot password?</Link>
						</div>
					</div>

					{status !== 200 && <p
						className="screen-signin__error"
						dangerouslySetInnerHTML={{ __html: sanitize(errorMessage) }}
					/>}

					<Button
						type="submit"
						label="Log In"
						variable="info"
						loading={loading}
						className="centered-button"
					/>
				</form>
			</div>
		</ErrorBoundary>
	);
};

export default SignIn;
